import { t } from "i18next";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { toast } from "@/hooks/use-toast";
import { useAccountSettings } from "@/hooks/user/useAccountSettings";
import type { AccountSettingSchemaType } from "@/schemas/accountSettings.schema";
import { useAuthStore } from "@/store/auth.store";
import { useOccupationsStore } from "@/store/occupations.store";
import { ProfileForm } from "./components/ProfileForm";

export const ProfileSettings = (): JSX.Element => {
  const { updateProfile, updatePreferences, refreshAccount, loading, result } =
    useAccountSettings();
  const { userData } = useAuthStore();
  const {
    fetchAndSetOccupations,
    loading: loadingOccupations,
    occupations,
  } = useOccupationsStore();

  useEffect(() => {
    void fetchAndSetOccupations();
  }, [fetchAndSetOccupations]);

  const defaultValues = useMemo<AccountSettingSchemaType>(
    () => ({
      firstname: userData?.firstname || "",
      lastname: userData?.lastname || "",
      phone: userData?.phone || "",
      country: userData?.userPreference.country || "",
      state: userData?.userPreference.state || "",
      city: userData?.userPreference.city || "",
      occupation: userData?.userPreference.occupation || "",
      occupationId: 0,
    }),
    [userData],
  );

  useEffect(() => {
    if (result != null && !loading) {
      if (result) {
        toast({
          variant: "success",
          title: t("settings.profile.updatedToast.successful.title"),
          description: t(
            "settings.profile.updatedToast.successful.description",
          ),
        });
        void refreshAccount();
      } else {
        toast({
          variant: "destructive",
          title: t("settings.profile.updatedToast.error.title"),
          description: t("settings.profile.updatedToast.error.description"),
        });
      }
    }
  }, [result, loading, refreshAccount]);

  const handleSubmit = (data: Partial<AccountSettingSchemaType>): void => {
    if (userData) {
      const { firstname, lastname, phone, occupationId, country, state, city } =
        data;
      if (
        firstname !== undefined ||
        lastname !== undefined ||
        phone !== undefined
      ) {
        void updateProfile({ firstName: firstname, lastName: lastname, phone });
      } else {
        void updatePreferences({ occupationId, country, state, city });
      }
    }
  };

  return (
    <section>
      <ProfileForm
        defaultValues={defaultValues}
        loadingOccupations={loadingOccupations}
        occupations={occupations}
        onSubmit={handleSubmit}
      />
    </section>
  );
};
